import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import { useDomainServices } from "@/providers/DomainServices";
import { VizConfig } from "@/shared/config";
import { messagePathRepresentationMappings } from "@/shared/domain/topics";
import { isAbortError } from "@/shared/errors";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";
import { State } from "@/shared/state/visualization";
import { useDatasources } from "@/shared/state/visualization/hooks";

import { Timer } from "../timer";

import { EphemeralWorkspaceState } from "./EphemeralWorkspaceState";
import { useLayoutState } from "./useLayoutState";
import {
  emptyWorkspaceData,
  WorkspaceData,
  WorkspaceDataService,
} from "./WorkspaceDataService";
import { deriveWorkspaceSources } from "./workspaceSources";

export const useWorkspaceState = (): EphemeralWorkspaceState => {
  const domainServices = useDomainServices();
  const queryClient = useQueryClient();
  const datasources = useDatasources();

  const workspaceDataService = React.useMemo(
    () => new WorkspaceDataService(domainServices, queryClient),
    [domainServices, queryClient],
  );

  // Layout workspace state
  const layoutState = useLayoutState();

  // Domain data state
  const [workspaceData, setWorkspaceData] =
    React.useState<WorkspaceData>(emptyWorkspaceData);

  // Data loading and error state
  const [isDataLoading, setIsDataLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);

  // Workspace UI processing state
  const [isHydrating, setIsHydrating] = React.useState(true);

  // Panel selection state
  const [selectedPanelId, setSelectedPanelId] = React.useState<string | null>(
    null,
  );

  // Workspace snapshot for tracking changes
  const workspaceSnapshotRef = React.useRef({
    panels: VizConfig.default().panels,
    layout: VizConfig.default().layout,
  });

  // Panel data errors
  const [dataMatchingErrors, setDataMatchingErrors] = React.useState<
    EphemeralWorkspaceState["dataMatchingErrors"]
  >({});

  const timerRef = React.useRef(new Timer({ spans: [] }));

  const workspaceSources = React.useMemo(
    () =>
      deriveWorkspaceSources(
        workspaceData.files,
        workspaceData.topics,
        workspaceData.sessions,
      ),
    [workspaceData.files, workspaceData.topics, workspaceData.sessions],
  );

  React.useEffect(
    function initializeTimer() {
      // Immediately reset the timer when the time spans change.
      timerRef.current.reset();

      // One span per source, the same span that bounds where a plot panel
      // draws its playhead and what a click on it can seek to. Playback
      // therefore stops at a session's clip range, and the files of one
      // session give a single span from the earliest start to the latest end
      // instead of one span each.
      const spans = workspaceSources.map((source): [bigint, bigint] => [
        source.startTime,
        source.endTime,
      ]);
      const mergedAndSorted = Timer.sortAndMergeSpans(spans);
      timerRef.current.updateTimeSpans(mergedAndSorted);
    },
    [workspaceSources],
  );

  React.useEffect(
    function loadWorkspaceData() {
      if (datasources.length === 0) {
        // Drop what the previous datasources loaded. Leaving it in place would
        // keep their files on screen, and keep raising banners for sessions
        // that are no longer part of this workspace.
        setWorkspaceData(emptyWorkspaceData());
        setError(null);
        setIsDataLoading(false);
        return;
      }

      setIsDataLoading(true);

      const abortController = new AbortController();

      workspaceDataService
        .fetchWorkspaceData(datasources, abortController)
        .then((fetched) => {
          setError(null);
          setWorkspaceData(fetched);
        })
        .catch((err: Error) => {
          if (abortController.signal.aborted || isAbortError(err)) {
            return;
          }
          // The user sees a workspace-level error, which says nothing about
          // which datasource failed to resolve; report it so a failed load is
          // diagnosable after the fact.
          ErrorMonitoringService.captureError(err);
          setError(err);
        })
        .finally(() => {
          if (!abortController.signal.aborted) {
            setIsDataLoading(false);
          }
        });

      return () => abortController.abort();
    },
    [datasources, workspaceDataService],
  );

  const panelHandlers = React.useMemo(
    () => ({
      selectPanel: (panelId: string) => setSelectedPanelId(panelId),
      clearPanelSelection: () => setSelectedPanelId(null),
      clearPanelSelectionIfMatches: (panelId: string) => {
        setSelectedPanelId((current) => (current === panelId ? null : current));
      },
    }),
    [],
  );

  const workspaceHandlers = React.useMemo(
    () => ({
      setWorkspaceSnapshot: (snapshot: {
        panels: State["panels"];
        layout: State["layout"];
      }) => {
        workspaceSnapshotRef.current = snapshot;
      },
    }),
    [],
  );

  // Ephemeral panel state (viewport, filters, etc.)
  const ephemeralPanelStateRef = React.useRef(
    new Map<string, Record<string, unknown>>(),
  );

  const updateEphemeralPanelState = React.useCallback(
    (panelId: string, updates: Record<string, unknown>) => {
      const existing = ephemeralPanelStateRef.current.get(panelId) ?? {};
      ephemeralPanelStateRef.current.set(panelId, { ...existing, ...updates });
    },
    [],
  );

  // Each topic's representation mappings, computed once from topics so media
  // panels can open sources without a per-topic representations request.
  const mappingsByTopicId = React.useMemo(
    () =>
      new Map(
        workspaceData.topics.map((topic) => [
          topic.topic_id,
          messagePathRepresentationMappings(topic),
        ]),
      ),
    [workspaceData.topics],
  );

  return React.useMemo(
    (): EphemeralWorkspaceState => ({
      // Server state from WorkspaceDataService.fetchWorkspaceData
      ...workspaceData,
      sources: workspaceSources,
      mappingsByTopicId,
      isDataLoading: layoutState.isLoading || isDataLoading,
      error,
      layoutError: layoutState.error,

      // Panel selection
      selectedPanelId,
      ...panelHandlers,

      // Timer
      timer: timerRef.current,

      // Layout UI state
      activeLayout: layoutState.activeLayout,
      setActiveLayout: layoutState.setActiveLayout,
      skipLayoutRehydration: layoutState.skipLayoutRehydration,
      isHydrating,
      setIsHydrating,
      workspaceSnapshot: workspaceSnapshotRef.current,
      ...workspaceHandlers,

      // Panel data errors
      dataMatchingErrors,
      setDataMatchingErrors,

      // Ephemeral panel state
      ephemeralPanelState: ephemeralPanelStateRef.current,
      updateEphemeralPanelState,
    }),
    [
      layoutState.activeLayout,
      layoutState.isLoading,
      layoutState.error,
      layoutState.setActiveLayout,
      layoutState.skipLayoutRehydration,
      workspaceData,
      workspaceSources,
      mappingsByTopicId,
      isDataLoading,
      error,
      selectedPanelId,
      panelHandlers,
      workspaceHandlers,
      isHydrating,
      dataMatchingErrors,
      updateEphemeralPanelState,
    ],
  );
};
